"use client";

/**
 * Listings the person brought here from other sites, with their
 * analytics (GET /api/me/tracked, backend services/tracked_analytics).
 * Shared by the hub and /listing-analytics; ``refresh`` after a change.
 */
import { useCallback, useEffect, useState } from "react";
import { useUndo } from "@/lib/undo";
import { useT } from "@/lib/locale-context";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { onHubBoot } from "@/lib/hub-boot";

export interface TrackedItem {
  id: number;
  listing_id: string;
  source_url: string | null;
  added_via: "url" | "seller";
  created_at: string;
  price_at_add: number | null;
  indexed: boolean;
  status?: string;
  source?: string;
  price?: number | null;
  ppm?: number | null;
  leased?: boolean | null;
  room_count?: number | null;
  area_m2?: number | null;
  floor?: number | null;
  total_floors?: number | null;
  location_name?: string | null;
  location_id?: string | null;
  complex_name?: string | null;
  complex_slug?: string | null;
  thumbnail_url?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  district?: string | null;
  first_seen_at?: string | null;
  days_on_market?: number;
  model?: { price: number; pct: number | null; tier: string | null } | null;
  segment?: { median_ppm: number; vs_pct: number; n: number; cheaper: number; price_at_median: number | null; new_cheaper_7d: number } | null;
  history?: { first: number | null; cuts: number; points: { t: string | null; price: number }[] };
  tts?: { median_days: number | null; p75: number | null } | null;
  dup_others?: number;
  views_30d?: number;
  /** The last four weeks' distinct viewers, oldest week first. */
  views_weeks?: number[];
  seller_import?: boolean;
}

export interface TrackedPayload {
  items: TrackedItem[];
  summary: {
    count: number;
    active: number;
    avg_model_gap_pct: number | null;
    over_model: number;
    views_30d: number;
    stale: number;
  };
  limit: number;
}

const store: {
  uid: string | null;
  data: TrackedPayload | null;
  /** One read in flight, shared by every caller (the build is heavy). */
  pending: Promise<void> | null;
  listeners: Set<() => void>;
} = { uid: null, data: null, pending: null, listeners: new Set() };

function load(uid: string): Promise<void> {
  if (store.pending) return store.pending;
  store.pending = fetchTracked(uid).finally(() => { store.pending = null; });
  return store.pending;
}

const EMPTY: TrackedPayload = {
  items: [],
  summary: { count: 0, active: 0, avg_model_gap_pct: null, over_model: 0, views_30d: 0, stale: 0 },
  limit: 200,
};

async function fetchTracked(uid: string) {
  let d: TrackedPayload = EMPTY;
  try {
    const r = await fetch(`${API_BASE}/me/tracked`, { credentials: "include" });
    if (r.ok) d = (await r.json()) as TrackedPayload;
  } catch {
    /* a failed read renders the empty block with the add form, not a skeleton that stays */
  }
  if (store.uid === uid) {
    store.data = d;
    store.listeners.forEach((l) => l());
  }
}

// The hub's bootstrap carries an agent's list (their hero is about it):
// it is the store's, unless a read of the store's own is on its way.
onHubBoot((b) => {
  if (!b.tracked || store.pending || (store.uid === b.user_id && store.data)) return;
  store.uid = b.user_id;
  store.data = b.tracked as TrackedPayload;
  store.listeners.forEach((l) => l());
});

export type TrackError = "unsupported" | "limit" | "no_seller" | "network" | "category" | "not_found";

async function post(path: string, body: unknown): Promise<{ ok: true; data: Record<string, unknown> } | { ok: false; code: TrackError }> {
  try {
    const r = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return { ok: false, code: (d?.detail?.code as TrackError) || "network" };
    return { ok: true, data: d };
  } catch {
    return { ok: false, code: "network" };
  }
}

/** ``lazy``: do not read the list on mount (a page that only adds, like
 *  /elan's "is this yours?", must not run the analytics build per view). */
export function useTracked({ lazy = false }: { lazy?: boolean } = {}) {
  const undo = useUndo();
  const t = useT();
  const { user } = useAuth();
  const [, bump] = useState(0);
  const uid = user?.id ?? null;

  useEffect(() => {
    const l = () => bump((v) => v + 1);
    store.listeners.add(l);
    return () => { store.listeners.delete(l); };
  }, []);

  useEffect(() => {
    if (!uid) return;
    if (store.uid !== uid) { store.uid = uid; store.data = null; }
    if (!store.data && !lazy) void load(uid);
  }, [uid, lazy]);

  // After a change: a read that started before it is stale, so wait it
  // out and read again.
  const refresh = useCallback(async () => {
    if (!uid) return;
    if (store.pending) await store.pending;
    await load(uid);
  }, [uid]);

  const add = useCallback(async (url: string) => {
    const r = await post("/me/tracked", { url });
    if (r.ok) await refresh();
    return r;
  }, [refresh]);

  const importSeller = useCallback(async (listingId: string) => {
    const r = await post("/me/tracked/seller", { listing_id: listingId });
    if (r.ok) await refresh();
    return r;
  }, [refresh]);

  /** A pending link: read the page now instead of waiting for a pass. */
  const recheck = useCallback(async (id: number) => {
    const r = await post(`/me/tracked/${id}/refresh`, {});
    if (r.ok) await refresh();
    return r;
  }, [refresh]);

  const remove = useCallback(async (id: number) => {
    if (store.data) {
      store.data = { ...store.data, items: store.data.items.filter((i) => i.id !== id) };
      store.listeners.forEach((l) => l());
    }
    undo.schedule({
      key: `tracked:${id}`,
      message: t("undo.tracked_removed"),
      commit: async () => {
        await fetch(`${API_BASE}/me/tracked/${id}`, { method: "DELETE", credentials: "include", keepalive: true }).catch(() => {});
        await refresh();
      },
      undo: () => refresh(),
    });
  }, [refresh, undo, t]);

  return { data: uid && store.uid === uid ? store.data : null, add, importSeller, remove, recheck, refresh };
}

/* ------------------------------------------------------------------ */
/* The tracked links alone (no analytics build), for a listing page    */
/* ------------------------------------------------------------------ */

interface TrackedRef { id: number; listing_id: string; source_url: string | null }
const idsStore: { uid: string | null; items: TrackedRef[] | null; pending: Promise<void> | null; listeners: Set<() => void> } = { uid: null, items: null, pending: null, listeners: new Set() };

async function loadIds(uid: string) {
  idsStore.pending = (async () => {
    try {
      const r = await fetch(`${API_BASE}/me/tracked/ids`, { credentials: "include" });
      const d = r.ok ? ((await r.json()) as { items: TrackedRef[] }) : { items: [] };
      if (idsStore.uid === uid) idsStore.items = d.items ?? [];
    } catch { if (idsStore.uid === uid) idsStore.items = []; }
    idsStore.listeners.forEach((l) => l());
  })();
  await idsStore.pending;
  idsStore.pending = null;
}

const bare = (u: string) => u.replace(/^https?:\/\/(www\.)?/, "").split(/[?#]/)[0].replace(/\/$/, "");
const numId = (u: string) => (u.match(/(\d{5,})(?:[/?#]|$)/) ?? [])[1] ?? null;

/** Whether a link is already in the person's analytics (by its bare URL
 *  or, for the numeric sources, its id), and the row's id when it is.
 *  ``null`` while unknown. Reads a cheap ids list, never the build; a
 *  change made through ``useTracked`` refreshes it. */
export function useTrackedRef(url: string | null): { known: boolean; tracked: TrackedRef | null; refreshIds: () => Promise<void> } {
  const { user } = useAuth();
  const [, bump] = useState(0);
  const uid = user?.id ?? null;
  useEffect(() => {
    const l = () => bump((v) => v + 1);
    idsStore.listeners.add(l); store.listeners.add(l);
    return () => { idsStore.listeners.delete(l); store.listeners.delete(l); };
  }, []);
  useEffect(() => {
    if (!uid) return;
    if (idsStore.uid !== uid) { idsStore.uid = uid; idsStore.items = null; }
    if (!idsStore.items && !idsStore.pending) void loadIds(uid);
  }, [uid]);
  const refreshIds = useCallback(async () => { if (uid) await loadIds(uid); }, [uid]);
  if (!uid || !url) return { known: true, tracked: null, refreshIds };
  const items: TrackedRef[] | null = idsStore.uid === uid && idsStore.items
    ? idsStore.items
    : store.uid === uid && store.data ? store.data.items.map((i) => ({ id: i.id, listing_id: i.listing_id, source_url: i.source_url })) : null;
  if (!items) return { known: false, tracked: null, refreshIds };
  const b = bare(url); const n = numId(url);
  const hit = items.find((i) => (i.source_url && bare(i.source_url) === b) || (n != null && i.listing_id.endsWith(`:${n}`) && b.startsWith(i.listing_id.split(":")[0].replace("_", ".")))) ?? null;
  return { known: true, tracked: hit, refreshIds };
}
